/**
 * RefundRequests.ts (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * @file RefundRequests.ts
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

import { BASE_API_URL } from '../Config';
import { HttpClient } from '@abstract/abstractwebcommon-client/utils/HttpClient';
import { createLog } from './Logs';
import { IAPIEntityResponse } from '@abstract/abstractwebcommon-shared/interfaces/api';
import { defaultTableLimit } from '@abstract/abstractwebcommon-client/Constants';
import { paginationRequest } from '@abstract/abstractwebcommon-client/Pagination/paginationRequest';
import { IRefundRequest } from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/RefundRequest';

const httpClient = new HttpClient(BASE_API_URL, createLog);

const baseURLPath: string = '/refundRequests/';

/**
 * Create a refund request for a subscription. Used by the subscription owner.
 * @param subscriptionID Subscription ID the refund is requested for.
 * @param reason Reason provided by the user.
 * @returns Promise<IAPIEntityResponse<string>>.
 */
export const createRefundRequestService = (
  subscriptionID: string,
  reason: string
): Promise<IAPIEntityResponse<string>> => {
  return httpClient.post(`${baseURLPath}create/${subscriptionID}`, { reason });
};

/**
 * Get all refund requests with pagination. Admin only.
 * @param skip Skip value for pagination.
 * @param limit Limit value for pagination.
 * @param searchTerm Search term to find specific record.
 * @param sort Sort field and sort order.
 * @returns Promise<IAPIEntityResponse<IRefundRequest[]>>.
 */
export const getAllRefundRequestsService = (
  skip = 0,
  limit = defaultTableLimit,
  searchTerm = '',
  sort = {
    sortField: 'createdAt',
    sortOrder: -1
  }
): Promise<IAPIEntityResponse<IRefundRequest[]>> => {
  const url = paginationRequest(`admin/get/all`, {
    skip,
    limit,
    searchTerm,
    filter: {}
  });
  return httpClient.post(`${baseURLPath}${url}`, { sort });
};

/**
 * Approve a pending refund request and execute the refund. Admin only.
 * @param refundRequestID Refund request ID to approve.
 * @returns Promise<IAPIEntityResponse<string>>.
 */
export const approveRefundRequestService = (
  refundRequestID: string
): Promise<IAPIEntityResponse<string>> => {
  return httpClient.post(`${baseURLPath}admin/approve/${refundRequestID}`, {});
};

/**
 * Reject a pending refund request. Admin only.
 * @param refundRequestID Refund request ID to reject.
 * @param rejectionReason Reason for the rejection.
 * @returns Promise<IAPIEntityResponse<string>>.
 */
export const rejectRefundRequestService = (
  refundRequestID: string,
  rejectionReason: string
): Promise<IAPIEntityResponse<string>> => {
  return httpClient.post(`${baseURLPath}admin/reject/${refundRequestID}`, {
    rejectionReason
  });
};
